
import React from 'react';
import { ClientDetailsSkeleton } from "./components/ClientDetailsSkeleton";
import { ClientDetailsContent } from "./components/ClientDetailsContent";
import { toast } from "sonner";
import { Client } from '@/types/types';
import { useClientsSupabaseStore } from '@/store/api/clientsSupabaseStore';
import { useUserStore } from '@/store/userStore';

interface ClientDetailsTabProps {
  client: Client;
  isLoading?: boolean;
}

export function ClientDetailsTab({ client, isLoading = false }: ClientDetailsTabProps) {
  const gymId = useUserStore(state => state.gymId);
  const [error, setError] = React.useState<string | null>(null);
  const fetchClientById = useClientsSupabaseStore(state => state.fetchClientById);
  
  // Memoize initialData to prevent unnecessary re-renders
  // Only changes when client.id changes
  const initialData = React.useMemo(() => ({
    email: client?.email || '',
    phone: client?.phone || '',
    address: client?.address || '',
    emergencyContact: client?.emergencyContact || '',
    birthDate: client?.birthDate || '',
    clothingSize: client?.clothingSize || '',
    notes: client?.notes || '',
  }), [client?.id, client?.email, client?.phone, client?.address, client?.emergencyContact, client?.birthDate, client?.clothingSize, client?.notes]);
  
  // Function to refresh the user profile - now actually refreshes the store
  const refreshUserProfile = React.useCallback(() => {
    if (client?.id) {
      console.log("Refreshing client data from store for:", client.id);
      fetchClientById(client.id);
    }
  }, [client?.id, fetchClientById]);
  
  // Error handling
  React.useEffect(() => {
    if (error) {
      console.error("ClientDetailsTab error:", error);
      toast.error(`Error loading client details: ${error}`);
    }
  }, [error]);
  
  // Get the user ID from the client data
  const userId = client?.id ? String(client.id) : '';

  
  // Only show loading if we don't have any client data yet
  if (isLoading && !client) {
    return <ClientDetailsSkeleton />;
  }
  
  if (!client) {
    return <div className="text-center py-4">Client data not available</div>;
  }
  
  return (
    <ClientDetailsContent 
      key={`${gymId ?? ''}:${client.id}`}
      userId={userId} 
      refreshUserProfile={refreshUserProfile} 
      initialData={initialData}
    />
  );
}
